/**
 * O que faz: renderiza o modal de criação ou reagendamento de retorno programado no chat.
 * Onde usa: LeadInfoSidebar e LeadReturnSummary na lateral da conversa.
 * Conexões: react-hook-form, Zod, hooks de retorno, responsáveis e helper de fuso de São Paulo.
 * Tabelas: lead_returns e leads via RPCs create_lead_return, create_lead_return_from_kanban e transition_lead_return; não faz insert/update direto.
 * Data: 24/08/2026
 */

import React, { useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { CalendarClock, CheckCircle2, Info, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { convertSaoPauloToUTC } from '@/utils/timezoneUtils';
import { useCreateLeadReturn, useCreateLeadReturnFromKanban, useTransitionLeadReturn } from './useLeadReturns';
import { useReturnAssignees } from './useReturnAssignees';
import type { LeadReturn, ReturnExecutionMode, ReturnExecutorType } from './returnTypes';

const returnFormSchema = z.object({
  date: z.string().min(1, 'Escolha a data.'),
  time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/u, 'Informe um horário válido.'),
  reason: z.string().trim().min(1, 'Informe o motivo do retorno.').max(240),
  context_note: z.string().max(2000),
  executor_type: z.enum(['human', 'internal_ai', 'external_ai']),
  execution_mode: z.enum(['draft_only', 'automatic']),
  responsible_profile_id: z.string().uuid().nullable(),
}).superRefine((value, context) => {
  // Validação de negócio: retorno humano sempre vira tarefa da equipe, então não pode disparar mensagem automática.
  if (value.executor_type === 'human' && value.execution_mode !== 'draft_only') {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['execution_mode'],
      message: 'Retorno humano não usa envio automático.',
    });
  }
});

type ReturnFormValues = z.infer<typeof returnFormSchema>;

interface ReturnScheduleDialogProps {
  leadId: string;
  clinicaId?: string | null;
  leadName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCompleted?: () => void;
  initialReturn?: LeadReturn | null;
  kanbanTargetStageId?: string | null;
}

const EXECUTOR_OPTIONS: Array<{
  value: ReturnExecutorType;
  label: string;
  description: string;
}> = [
  {
    value: 'human',
    label: 'Humano',
    description: 'Aparece na agenda da equipe e abre o chat no dia.',
  },
  {
    value: 'internal_ai',
    label: 'IA do sistema',
    description: 'Usa o provedor configurado no CRM.',
  },
  {
    value: 'external_ai',
    label: 'IA externa',
    description: 'Fica disponível para a integração autorizada da clínica.',
  },
];

const EXECUTION_MODE_OPTIONS: Array<{
  value: ReturnExecutionMode;
  label: string;
  description: string;
}> = [
  {
    value: 'draft_only',
    label: 'Preparar rascunho',
    description: 'Exige aprovação humana.',
  },
  {
    value: 'automatic',
    label: 'Enviar automaticamente',
    description: 'Só aparece para IA e pede confirmação explícita.',
  },
];

const EMPTY_ASSIGNEE_VALUE = '__sem_responsavel__';

const formatReturnForSaoPauloInput = (utcDateTime: string) => {
  // Motivo: `datetime-local` trabalha sem offset; formatar por Intl em São Paulo evita depender do fuso do computador.
  const formattedParts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(utcDateTime));

  const partByType = Object.fromEntries(formattedParts.map(part => [part.type, part.value]));

  return {
    date: `${partByType.year}-${partByType.month}-${partByType.day}`,
    time: `${partByType.hour}:${partByType.minute}`,
  };
};

const buildDefaultValues = (initialReturn?: LeadReturn | null): ReturnFormValues => {
  if (!initialReturn) {
    return {
      date: '',
      time: '',
      reason: '',
      context_note: '',
      executor_type: 'human',
      execution_mode: 'draft_only',
      responsible_profile_id: null,
    };
  }

  const { date, time } = formatReturnForSaoPauloInput(initialReturn.scheduled_for);

  return {
    date,
    time,
    reason: initialReturn.reason,
    context_note: initialReturn.context_note ?? '',
    executor_type: initialReturn.executor_type,
    execution_mode: initialReturn.execution_mode,
    responsible_profile_id: initialReturn.responsible_profile_id,
  };
};

export function ReturnScheduleDialog({
  leadId,
  clinicaId,
  leadName,
  open,
  onOpenChange,
  onCompleted,
  initialReturn = null,
  kanbanTargetStageId = null,
}: ReturnScheduleDialogProps) {
  // Os responsáveis e as validações das ações usam o mesmo tenant do contato mostrado na ficha.
  const createLeadReturn = useCreateLeadReturn(clinicaId);
  const createLeadReturnFromKanban = useCreateLeadReturnFromKanban(clinicaId);
  const transitionLeadReturn = useTransitionLeadReturn(clinicaId);
  const { data: assignees = [], isLoading: isLoadingAssignees, error: assigneesError } = useReturnAssignees(clinicaId);
  const [automaticConfirmed, setAutomaticConfirmed] = useState(false);

  const form = useForm<ReturnFormValues>({
    resolver: zodResolver(returnFormSchema),
    defaultValues: buildDefaultValues(initialReturn),
    mode: 'onSubmit',
  });

  const executorType = form.watch('executor_type');
  const executionMode = form.watch('execution_mode');
  const isReschedule = Boolean(initialReturn);
  const isSubmitting = createLeadReturn.isPending || createLeadReturnFromKanban.isPending || transitionLeadReturn.isPending;
  const canUseAutomaticMode = executorType !== 'human';
  const readonlyFieldClassName = isReschedule ? 'bg-muted text-muted-foreground' : '';

  useEffect(() => {
    if (!open) {
      return;
    }

    // Motivo: abrir para outro lead/retorno precisa limpar estado visual antigo do formulário e da confirmação automática.
    form.reset(buildDefaultValues(initialReturn));
    setAutomaticConfirmed(false);
  }, [form, initialReturn, open]);

  useEffect(() => {
    if (executorType !== 'human') {
      return;
    }

    // Motivo: se o usuário voltar para humano, o modo automático deixa de existir para impedir payload incoerente.
    form.setValue('execution_mode', 'draft_only', { shouldValidate: true });
    setAutomaticConfirmed(false);
  }, [executorType, form]);

  const dialogDescription = useMemo(() => {
    if (isReschedule) {
      return `Reagende o retorno de ${leadName} mantendo a trava de atualização mais recente.`;
    }

    if (kanbanTargetStageId) {
      return `Defina data e motivo para mover ${leadName} à coluna de retorno vinculada ao Kanban.`;
    }

    return `Defina quando ${leadName} deve voltar para a agenda da equipe ou para a fila autorizada de IA.`;
  }, [isReschedule, kanbanTargetStageId, leadName]);

  const validateFutureDateTime = (values: ReturnFormValues) => {
    // Validação de negócio: retorno programado só faz sentido no futuro; se falhar, a tela mantém o usuário no formulário.
    const scheduledForUtc = convertSaoPauloToUTC(`${values.date}T${values.time}`);

    if (new Date(scheduledForUtc).getTime() <= Date.now()) {
      form.setError('time', { type: 'manual', message: 'Escolha uma data e hora futuras.' });
      return null;
    }

    return scheduledForUtc;
  };

  const handleSubmit = async (values: ReturnFormValues) => {
    const scheduledForUtc = validateFutureDateTime(values);

    if (!scheduledForUtc) {
      return;
    }

    if (!isReschedule && values.execution_mode === 'automatic' && !automaticConfirmed) {
      // Validação de segurança: envio automático exige confirmação explícita para evitar disparo não revisado por engano.
      form.setError('execution_mode', {
        type: 'manual',
        message: 'Confirme o envio automático antes de agendar.',
      });
      return;
    }

    if (initialReturn) {
      await transitionLeadReturn.mutateAsync({
        return_id: initialReturn.id,
        target_status: 'pending',
        expected_updated_at: initialReturn.updated_at,
        scheduled_for: scheduledForUtc,
      });
    } else {
      const returnPayload = {
        lead_id: leadId,
        scheduled_for: scheduledForUtc,
        reason: values.reason.trim(),
        context_note: values.context_note.trim(),
        executor_type: values.executor_type,
        execution_mode: values.execution_mode,
        responsible_profile_id: values.responsible_profile_id,
      };

      // Motivo: quando o modal nasceu de um drop, uma única RPC salva a tarefa e a nova etapa.
      // Consequência: se a validação falhar, o banco faz rollback e o card continua na coluna anterior.
      // Regra de negócio: a coluna vinculada representa retornos com data e motivo, nunca um movimento solto.
      if (kanbanTargetStageId) {
        await createLeadReturnFromKanban.mutateAsync({
          ...returnPayload,
          etapa_kanban_id: kanbanTargetStageId,
        });
      } else {
        await createLeadReturn.mutateAsync(returnPayload);
      }
    }

    // Motivo: o Pipeline precisa atualizar somente as colunas afetadas depois que a RPC confirmou a transação.
    onCompleted?.();
    onOpenChange(false);
  };

  // Decisão de interface: manter o modal compacto, com data como foco inicial, grade 1 coluna em 390px e 2/3 colunas em desktop.
  // Motivo: a lateral do chat já usa cartões claros, verde esmeralda e raios arredondados; o retorno precisa parecer parte do fluxo atual.
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] w-[calc(100vw-2rem)] max-w-2xl overflow-y-auto rounded-2xl border-border p-0 shadow-xl sm:rounded-2xl">
        <DialogHeader className="border-b border-border px-5 pb-4 pt-5 text-left sm:px-6">
          <DialogTitle className="flex items-center gap-2 text-lg font-bold text-foreground">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent text-accent-foreground">
              <CalendarClock className="h-4 w-4" />
            </span>
            {isReschedule ? 'Reagendar retorno' : 'Agendar retorno'}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {dialogDescription}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5 px-5 py-5 sm:px-6">
            <div className="grid gap-3 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        autoFocus
                        className="h-11 rounded-xl border-border focus:border-primary/40 focus:ring-2 focus:ring-[rgba(184,218,85,0.28)]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Horário</FormLabel>
                    <FormControl>
                      <Input
                        type="time"
                        className="h-11 rounded-xl border-border focus:border-primary/40 focus:ring-2 focus:ring-[rgba(184,218,85,0.28)]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Motivo do retorno</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Ex.: cliente pediu contato após receber o orçamento"
                      readOnly={isReschedule}
                      className={cn('h-11 rounded-xl border-border focus:border-primary/40 focus:ring-2 focus:ring-[rgba(184,218,85,0.28)]', readonlyFieldClassName)}
                      {...field}
                    />
                  </FormControl>
                  {isReschedule ? (
                    <p className="text-xs text-muted-foreground">
                      Apenas data e horário serão salvos neste reagendamento.
                    </p>
                  ) : null}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="context_note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Observações para o atendimento</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Contexto útil para quem assumir o retorno"
                      readOnly={isReschedule}
                      className={cn('min-h-[92px] rounded-xl border-border text-sm focus:border-primary/40 focus:ring-2 focus:ring-[rgba(184,218,85,0.28)]', readonlyFieldClassName)}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="responsible_profile_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Responsável</FormLabel>
                  <FormControl>
                    <select
                      value={field.value ?? EMPTY_ASSIGNEE_VALUE}
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        field.onChange(nextValue === EMPTY_ASSIGNEE_VALUE ? null : nextValue);
                      }}
                      disabled={isReschedule || isLoadingAssignees || Boolean(assigneesError)}
                      className="flex h-11 w-full rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground shadow-sm outline-none transition-all focus:border-primary/40 focus:ring-2 focus:ring-[rgba(184,218,85,0.28)] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value={EMPTY_ASSIGNEE_VALUE}>
                        {isLoadingAssignees ? 'Carregando responsáveis...' : 'Sem responsável definido'}
                      </option>
                      {assignees.map(assignee => (
                        <option key={assignee.id} value={assignee.id}>
                          {assignee.nome_completo || 'Usuário sem nome'}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  {isReschedule ? (
                    <p className="text-xs text-muted-foreground">
                      O responsável atual fica somente para consulta.
                    </p>
                  ) : null}
                  {assigneesError ? (
                    <p className="text-sm font-medium text-destructive">
                      Não foi possível carregar os responsáveis.
                    </p>
                  ) : null}
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-3">
              <FormField
                control={form.control}
                name="executor_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Executor do retorno</FormLabel>
                    <div className="grid gap-2 sm:grid-cols-3">
                      {EXECUTOR_OPTIONS.map(option => (
                        <button
                          key={option.value}
                          type="button"
                          disabled={isReschedule}
                          onClick={() => field.onChange(option.value)}
                          className={cn(
                            'rounded-xl border p-3 text-left transition-all focus:outline-none focus:ring-2 focus:ring-[rgba(184,218,85,0.28)] disabled:cursor-not-allowed disabled:opacity-70',
                            field.value === option.value
                              ? 'border-primary/40 bg-accent text-accent-foreground'
                              : 'border-border bg-card text-foreground/80 hover:bg-muted',
                          )}
                        >
                          <span className="block text-sm font-semibold">{option.label}</span>
                          <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">{option.description}</span>
                        </button>
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="execution_mode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Modo de execução</FormLabel>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {EXECUTION_MODE_OPTIONS
                        .filter(option => option.value !== 'automatic' || canUseAutomaticMode)
                        .map(option => (
                          <button
                            key={option.value}
                            type="button"
                            disabled={isReschedule}
                            onClick={() => field.onChange(option.value)}
                            className={cn(
                              'rounded-xl border p-3 text-left transition-all focus:outline-none focus:ring-2 focus:ring-[rgba(184,218,85,0.28)] disabled:cursor-not-allowed disabled:opacity-70',
                              field.value === option.value
                                ? 'border-primary/40 bg-accent text-accent-foreground'
                                : 'border-border bg-card text-foreground/80 hover:bg-muted',
                            )}
                          >
                            <span className="block text-sm font-semibold">{option.label}</span>
                            <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">{option.description}</span>
                          </button>
                        ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {isReschedule ? (
              <div className="flex gap-2 rounded-xl border border-border bg-muted p-3 text-sm text-foreground/80">
                <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <p>Motivo, contexto, executor, modo e responsável não são alterados no reagendamento.</p>
              </div>
            ) : executionMode === 'automatic' ? (
              <label className="flex gap-3 rounded-xl border border-warning/30 bg-warning-subtle p-3 text-sm text-warning-text">
                <input
                  type="checkbox"
                  checked={automaticConfirmed}
                  onChange={(event) => setAutomaticConfirmed(event.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-warning/30 text-accent-foreground focus:ring-[rgba(184,218,85,0.28)]"
                />
                <span>
                  Confirmo que este retorno poderá ser enviado automaticamente pela IA configurada para a clínica.
                </span>
              </label>
            ) : (
              <div className="flex gap-2 rounded-xl border border-primary/40 bg-accent/70 p-3 text-sm text-accent-foreground">
                <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <p>O modo rascunho prepara a próxima mensagem e mantém a aprovação humana antes do envio.</p>
              </div>
            )}

            <DialogFooter className="gap-2 border-t border-border pt-5 sm:gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
                className="h-10 rounded-xl border-border"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || isLoadingAssignees}
                className="h-10 rounded-xl bg-primary text-primary-foreground hover:bg-emerald-700"
              >
                {isSubmitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                )}
                {isReschedule ? 'Salvar reagendamento' : 'Agendar retorno'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
